import React from 'react';
import LifecycleBadge from './LifecycleBadge';
import SourceBadge from './SourceBadge';
import UpstreamBadge from './UpstreamBadge';
import type { PlaybookEntity, UpstreamChanges } from '../types/playbook.types';

interface PlaybookCardProps {
  playbook: PlaybookEntity;
  upstream?: UpstreamChanges;
  indent?: boolean;
  onView: (id: string) => void;
  onClone: (id: string) => void;
  onPublish?: (id: string) => void;
  onArchive?: (id: string) => void;
  onUpstreamClick?: (id: string) => void;
}

const PlaybookCard: React.FC<PlaybookCardProps> = ({
  playbook, upstream, indent, onView, onClone, onPublish, onArchive, onUpstreamClick,
}) => {
  const statusCount = playbook.status_count ?? playbook.statuses?.length ?? 0;
  const transitionCount = playbook.transition_count ?? playbook.transitions?.length ?? 0;
  const isDraft = playbook.lifecycle === 'draft';
  const isSystem = playbook.is_system;

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-3 hover:border-gray-300 transition-colors dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 ${
        indent ? 'ml-6 border-l-2 border-l-lex-accent' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => onView(playbook.id)}
              className="text-sm font-semibold text-gray-900 hover:text-lex-accent truncate dark:text-white"
            >
              {playbook.name}
            </button>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">v{playbook.version}</span>
            <LifecycleBadge lifecycle={playbook.lifecycle} />
            <SourceBadge source={playbook.source} />
            {isSystem && (
              <span className="inline-flex items-center rounded-full bg-lex-accent-soft px-2 py-0.5 text-[10px] font-medium text-lex-accent">
                System
              </span>
            )}
            {upstream && (
              <UpstreamBadge upstream={upstream} onClick={() => onUpstreamClick?.(playbook.id)} />
            )}
          </div>

          {playbook.description && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{playbook.description}</p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[10px] text-gray-500 dark:text-gray-400">
            <span>{statusCount} statuses</span>
            <span>{transitionCount} transitions</span>
            {playbook.practice_areas?.length > 0 && (
              <span>{playbook.practice_areas.join(', ')}</span>
            )}
            {playbook.jurisdiction_codes?.length > 0 && (
              <span>{playbook.jurisdiction_codes.join(', ')}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onView(playbook.id)}
            className="rounded-md border border-gray-300 px-2 py-1 text-[10px] font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            View
          </button>
          <button
            type="button"
            onClick={() => onClone(playbook.id)}
            className="rounded-md border border-lex-accent px-2 py-1 text-[10px] font-medium text-lex-accent hover:bg-lex-state-hover"
          >
            Clone
          </button>
          {!isSystem && isDraft && onPublish && (
            <button
              type="button"
              onClick={() => onPublish(playbook.id)}
              className="rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-emerald-700"
            >
              Publish
            </button>
          )}
          {!isSystem && playbook.lifecycle !== 'archived' && onArchive && (
            <button
              type="button"
              onClick={() => onArchive(playbook.id)}
              className="rounded-md border border-rose-300 px-2 py-1 text-[10px] font-medium text-rose-600 hover:bg-rose-50 dark:border-rose-600 dark:text-rose-400 dark:hover:bg-rose-900/30"
            >
              Archive
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaybookCard;
